// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Burger, Group, AppShell as MantineAppShell, NavLink, ScrollArea, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMedplum } from '@medplum/react';
import { IconLogout } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import polarisLogo from '../../assets/polaris-logo.svg';

interface AppShellMenuLink {
  icon: ReactNode;
  label: string;
  href: string;
}

interface AppShellMenu {
  links: AppShellMenuLink[];
}

interface AppShellProps {
  notifications?: ReactNode;
  menus?: AppShellMenu[];
  children: ReactNode;
}

export function AppShell({ notifications, menus, children }: AppShellProps): JSX.Element {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = (): void => {
    medplum
      .signOut()
      .catch(console.error)
      .finally(() => navigate('/signin'));
  };

  const isLinkActive = (href: string): boolean => {
    const [hrefPath] = href.split('?');
    return location.pathname === hrefPath || location.pathname.startsWith(`${hrefPath}/`);
  };

  if (!menus) {
    return (
      <MantineAppShell padding={0}>
        <MantineAppShell.Main bg="gray.0">{children}</MantineAppShell.Main>
      </MantineAppShell>
    );
  }

  return (
    <MantineAppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: 'md',
        collapsed: { mobile: !mobileOpened, desktop: false },
      }}
      padding={0}
    >
      <MantineAppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <UnstyledButton component={Link} to="/" aria-label="Home" style={{ cursor: 'pointer' }}>
            <img src={polarisLogo} alt="Polaris Sleep" style={{ height: 32, display: 'block' }} />
          </UnstyledButton>
          <Group gap="sm" wrap="nowrap">
            {notifications}
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="md" size="sm" aria-label="Toggle menu" />
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="xs">
        <MantineAppShell.Section grow component={ScrollArea}>
          {menus?.map((menu, mi) => (
            // Stable menu list, array index is intentional
            <Box key={mi} mb="sm">
              {menu.links.map((link) => {
                const active = isLinkActive(link.href);
                return (
                  <NavLink
                    key={link.href}
                    component={Link}
                    to={link.href}
                    label={link.label}
                    leftSection={link.icon}
                    active={active}
                    onClick={() => closeMobile()}
                    styles={{
                      root: { borderRadius: 'var(--mantine-radius-md)' },
                      label: {
                        color: 'var(--mantine-color-gray-9)',
                        fontWeight: active ? 700 : undefined,
                      },
                      section: {
                        color: active ? 'var(--mantine-color-gray-9)' : 'var(--mantine-color-gray-6)',
                      },
                    }}
                  />
                );
              })}
            </Box>
          ))}
        </MantineAppShell.Section>
        {medplum.getProfile() && (
          <MantineAppShell.Section>
            <NavLink label="Logout" leftSection={<IconLogout />} onClick={handleLogout} />
          </MantineAppShell.Section>
        )}
      </MantineAppShell.Navbar>

      <MantineAppShell.Main bg="gray.0">{children}</MantineAppShell.Main>
    </MantineAppShell>
  );
}
